/**
 * Poll
 *
 * A component for interactive polls in chat messages
 *
 * Primary responsibilities:
 * - Display a question with multiple voting options
 * - Allow users to vote on their preferred option
 * - Show real-time voting results with percentage bars
 * - Support responsive layout for mobile and desktop
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconChartBar, IconCheck, IconRefresh } from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'

interface PollOption {
  id: string
  text: string
  votes: number
}

interface PollProps {
  question: string
  options: PollOption[]
  className?: string
}

export const Poll: React.FC<PollProps> = ({ question, options: initialOptions, className }) => {
  const [options, setOptions] = useState<PollOption[]>(initialOptions)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [hasVoted, setHasVoted] = useState(false)

  const totalVotes = options.reduce((sum, option) => sum + option.votes, 0)

  const handleVote = (optionId: string) => {
    if (hasVoted) return

    setSelectedOption(optionId)
    setOptions((prev) =>
      prev.map((option) =>
        option.id === optionId ? { ...option, votes: option.votes + 1 } : option
      )
    )
    setHasVoted(true)
  }

  const resetVote = () => {
    if (!hasVoted || !selectedOption) return

    setOptions((prev) =>
      prev.map((option) =>
        option.id === selectedOption ? { ...option, votes: Math.max(0, option.votes - 1) } : option
      )
    )
    setSelectedOption(null)
    setHasVoted(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }}
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <motion.div
        className="flex items-center border-b p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <IconChartBar size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Poll</h3>
          <AnimatePresence>
            {hasVoted && (
              <motion.span
                className="text-muted-foreground ml-auto text-xs"
                initial={{ opacity: 0, scale: 0.8, x: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.8, x: -20 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <div className="p-4">
        <motion.h4
          className="mb-4 text-base font-medium"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          {question}
        </motion.h4>

        <div className="space-y-3">
          {options.map((option, index) => {
            const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0

            return (
              <motion.div
                key={option.id}
                className={cn(
                  'relative cursor-pointer rounded-md border p-3 transition-all',
                  selectedOption === option.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-border'
                )}
                onClick={() => handleVote(option.id)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: 0.3 + index * 0.1,
                  duration: 0.3,
                  ease: 'easeOut'
                }}
                whileHover={{
                  scale: hasVoted ? 1 : 1.02,
                  backgroundColor: hasVoted ? undefined : 'rgba(0,0,0,0.02)',
                  transition: { duration: 0.2 }
                }}
                whileTap={{
                  scale: hasVoted ? 1 : 0.98,
                  transition: { duration: 0.1 }
                }}
              >
                {/* Option text */}
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium">{option.text}</span>
                  <AnimatePresence>
                    {hasVoted && (
                      <motion.span
                        className="text-muted-foreground text-xs font-medium"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                      >
                        {percentage}%
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                {/* Progress bar (only visible after voting) */}
                <AnimatePresence>
                  {hasVoted && (
                    <motion.div
                      className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                      initial={{ opacity: 0, scaleX: 0 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0 }}
                      transition={{ delay: 0.1, duration: 0.4, ease: 'easeOut' }}
                      style={{ transformOrigin: 'left' }}
                    >
                      <motion.div
                        className={cn(
                          'h-full rounded-full',
                          selectedOption === option.id
                            ? 'bg-blue-500'
                            : 'bg-gray-400 dark:bg-gray-500'
                        )}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Checkmark for selected option */}
                <AnimatePresence>
                  {selectedOption === option.id && (
                    <motion.div
                      className="absolute right-2 top-2"
                      initial={{ opacity: 0, scale: 0, rotate: -180 }}
                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                      exit={{ opacity: 0, scale: 0, rotate: 180 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                    >
                      <IconCheck size={16} className="text-blue-500" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        <AnimatePresence>
          {hasVoted && (
            <motion.div
              className="mt-4 flex justify-end"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="ghost" size="sm" onClick={resetVote} className="text-xs">
                  <IconRefresh size={14} className="mr-1" />
                  Change Vote
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}
